
import paginator from '../paginator'
import loader from '../../partials/loader'
import scrollable from '../../partials/scrollable'

const productRecommendations = (data) => ({
    ...paginator(data.limit),
    ...loader,
    ...scrollable(data),
    url: data.url,
    viewedFrom: !data.viewed_from ? 'Recently Viewed' : data.viewed_from,
    initialised: false,
    originalUrl: false,
    impressionPosition: 1,
    // Set by the full-width band, which calls load() from x-intersect once it's near the viewport. Until then
    // init() does nothing, so a currency swap before it's in view just leaves it to load in the new currency.
    lazy: Boolean(data.lazy),
    async init() {
        if (this.lazy) {
            return
        }

        this.load()
    },
    load() {
        this.lazy = false

        if (!this.url || this.url.endsWith('?') || this.url.endsWith('=')) {
            return
        }

        this.startRecommending(this.originalUrl ? this.originalUrl : this.url)
    },
    async startRecommending(requestedUrl) {
        if ((this.pageData.length > 0 && this.originalUrl && this.initialised) || !requestedUrl) {
            return
        }

        this.originalUrl = requestedUrl
        this.initialised = false

        await this.startPagination(requestedUrl)

        if (this.pageData.length > 0) {
            const existingData = this.gatherExistingInformation(this.pageData)
            this.pageData = await this.getProductInformation(existingData.ids, existingData.data)
        }

        this.initialised = true
        this.stopLoader()
        this.handleScroll()

        // Page 2 and its prices (and so its impressions, still after page 1's) come in behind the strip instead of
        // ahead of it. The loader stays on meanwhile, so nextPage() doesn't move on before they're in.
        if (this.nextPageUrl) {
            this.startLoader()
            try {
                await this.preloadNextPage(this.nextPageUrl)

                if (this.preloaded.length > 0) {
                    await this.correctPreloadedData()
                }
            } finally {
                this.stopLoader()
            }
        }
    },
    async getProductInformation(products, existingData) {
        const mergedProductData = {}
        const finalDataset = []

        await window.api.getPricesAndStock(products).then((result) => {
            Object.values(result).forEach((product) => {
                mergedProductData[product.id] = {
                    ...product.data,
                    ...existingData[product.id],
                }
            })

            const impressionProducts = []
            const impressionProductsGA4 = []

            Object.values(mergedProductData).forEach((product) => {
                impressionProducts.push({
                    name: product.name,
                    id: product.reference,
                    price: product.price,
                    brand: product.manufacturer_name,
                    category: product.category_name,
                    list: this.viewedFrom,
                    position: this.impressionPosition,
                })

                impressionProductsGA4.push({
                    item_name: product.name,
                    item_id: product.reference,
                    price: product.price,
                    brand: product.manufacturer_name,
                    category: product.category_name,
                    item_list_name: this.viewedFrom,
                    item_list_id: this.viewedFrom.replace(/[^0-9a-z]/gi, '_').toLowerCase(),
                    index: this.impressionPosition - 1,
                })

                this.impressionPosition += 1
            })

            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    ecommerce: {
                        currencyCode: window.Alpine.store('currency').iso_code,
                        impressions: impressionProducts,
                    },
                    event: 'impression',
                },
            }))

            window.dispatchEvent(new CustomEvent('gtmdata', {
                detail: {
                    event: 'view_item_list',
                    ecommerce: {
                        item_list_id: this.viewedFrom.replace(/[^0-9a-z]/gi, '_').toLowerCase(),
                        item_list_name: this.viewedFrom,
                        currency: window.Alpine.store('currency').iso_code,
                        currencyCode: window.Alpine.store('currency').iso_code,
                        items: impressionProductsGA4,
                    },
                },
            }))
        })

        // reset the order to match the original order.
        products.forEach((product) => {
            finalDataset.push(mergedProductData[product.id])
        })

        return finalDataset
    },
    gatherExistingInformation(dataSet) {
        const productsToGet = []
        const dataToMerge = {}

        dataSet.forEach((product) => {
            productsToGet.push({
                id: product.id_product, quantity: 1, index: 0,
            })

            dataToMerge[product.id_product] = product
        })

        return { ids: productsToGet, data: dataToMerge }
    },
    async correctPreloadedData() {
        const existingData = this.gatherExistingInformation(this.preloaded)
        this.preloaded = await this.getProductInformation(existingData.ids, existingData.data)
    },
    async nextPage() {
        if (this.loading || !this.nextPageUrl) {
            return
        }

        this.startLoader()
        if (await this.loadNextPage()) {
            await this.correctPreloadedData()
        }
        this.stopLoader()
    },
})
document.addEventListener('alpine:init', () => {
    window.Alpine.data('productRecommendations', productRecommendations)
})
